import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import isPast from 'date-fns/isPast'
import Stack from 'ui/layout/Stack'
import {
  formatDateTimeJp,
  parseDatetimeSecondString,
} from 'ui/utils/dateFormatUtils'
import { Box } from '@mui/material'
import { Caption, Text1 } from 'ui/shorthands/Texts'
import type { PreReservationListItemProps } from 'types/med'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    margin: 'auto',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  listContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
    paddingBottom: 140, //NOTE: 新規作成ボタン+wovnタグで必要な高さ
  },
  item: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    boxSizing: 'border-box',
    height: 80,
    cursor: 'pointer',
  },
  itemExpired: {
    color: theme.palette.text.disabled,
    backgroundColor: theme.palette.background.paper,
  },
  itemActive: {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light,
  },
  row: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    justifyContent: 'space-between',
  },
  name: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  empty: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    padding: theme.spacing(2),
  },
}))

export const PreReservationListItem: React.FC<{
  preReservation: PreReservationListItemProps
  onClickItem?: (preReservation: PreReservationListItemProps) => void
  active?: boolean
}> = ({ preReservation, onClickItem, active }) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()

  const hasDateTime =
    !!preReservation?.provisional_reservation_datetime_start &&
    !!preReservation?.provisional_reservation_datetime_end

  const expiredDateObject: Date = useMemo(
    () => parseDatetimeSecondString(preReservation.access_key_expiration),
    [preReservation.access_key_expiration]
  )

  const isExpired: boolean = useMemo(
    () => isPast(expiredDateObject),
    [expiredDateObject]
  )

  const expiredTimeString = useMemo(
    () => formatDateTimeJp(expiredDateObject),
    [expiredDateObject]
  )

  return (
    <Box onClick={() => onClickItem?.(preReservation)}>
      <Stack
        borderBottom
        className={cx(
          classes.item,
          isExpired && classes.itemExpired,
          active && classes.itemActive
        )}
      >
        <div className={classes.row}>
          <span>{t('{{expiredTimeString}}まで', { expiredTimeString })}</span>
          {hasDateTime || (
            <Caption
              className={cx(
                isExpired && classes.itemExpired,
                active && classes.itemActive
              )}
            >
              {t('日時未指定')}
            </Caption>
          )}
        </div>
        <div className={classes.row}>
          <span className={classes.name}>
            {preReservation.applicant_name || t('氏名未指定')}
          </span>
        </div>
      </Stack>
    </Box>
  )
}

export const CreatingPreReservation: React.FC = () => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()
  return (
    <Box>
      <Stack borderBottom className={cx(classes.item, classes.itemActive)}>
        <div className={classes.row}>{t('日時未指定')}</div>
        <div className={classes.row}>
          <span className={classes.name}>{t('氏名未指定')}</span>
        </div>
      </Stack>
    </Box>
  )
}

export type PreReservationListProps = {
  /** 予約情報一覧 */
  preReservations?: PreReservationListItemProps[] | null

  /** 選択中のリストアイテム */
  selectedId: PreReservationListItemProps['id'] | null

  /** リストアイテムクリック時のイベントハンドラ */
  onClickItem: (preReservation: PreReservationListItemProps) => void

  /** 新規仮予約作成中かどうか */
  creating: boolean
}

const PreReservationList: React.FC<PreReservationListProps> = ({
  preReservations,
  onClickItem,
  selectedId,
  creating,
}: PreReservationListProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  return (
    <>
      <div className={classes.listContainer}>
        {preReservations?.length || creating ? (
          <>
            {preReservations?.map((preReservation) => (
              <PreReservationListItem
                key={preReservation.id}
                preReservation={preReservation}
                onClickItem={onClickItem}
                active={preReservation.id === selectedId}
              />
            ))}
            {creating && <CreatingPreReservation />}
          </>
        ) : (
          <div className={classes.empty}>
            <Text1>{t('仮予約がありません')}</Text1>
          </div>
        )}
      </div>
    </>
  )
}

PreReservationList.defaultProps = {
  creating: false,
}

export default PreReservationList
